import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Search, Loader2, FileText, Star, Clock, Brain, Users, FileSearch, FileCheck, Image as ImageIcon, MessageSquare, Sparkles, BookOpen, Send, AlertCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

const SearchInterface = ({ documentId }) => {
  // Tab management
  const [activeTab, setActiveTab] = useState('regular'); // 'regular', 'enhanced', 'summary'

  // Regular Search (Chat Interface)
  const [regularQuery, setRegularQuery] = useState('');
  const [regularSearching, setRegularSearching] = useState(false);
  const [regularChatHistory, setRegularChatHistory] = useState([]);
  const regularChatEndRef = useRef(null);

  // Enhanced Search
  const [enhancedQuery, setEnhancedQuery] = useState('');
  const [enhancedSearching, setEnhancedSearching] = useState(false);
  const [enhancedResults, setEnhancedResults] = useState(null);
  const [enhancedResultsList, setEnhancedResultsList] = useState([]);

  // Summary
  const [summarizing, setSummarizing] = useState(false);
  const [summary, setSummary] = useState(null);

  // Error handling
  const [error, setError] = useState(null);

  // Persona Analysis
  const [knowledgeLevel, setKnowledgeLevel] = useState('');
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [analyzingPersona, setAnalyzingPersona] = useState(false);

  // Auto-scroll chat to bottom
  useEffect(() => {
    if (activeTab === 'regular' && regularChatEndRef.current) {
      regularChatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [regularChatHistory, activeTab]);

  // Regular Search Handler (Chat Interface)
  const handleRegularSearch = async (e) => {
    e.preventDefault();
    if (!regularQuery.trim()) return;

    const userMessage = regularQuery.trim();
    setRegularQuery('');
    setRegularSearching(true);
    setError(null);

    // Add user message to chat
    const newUserMessage = {
      type: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setRegularChatHistory(prev => [...prev, newUserMessage]);

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/search/${documentId}`, {
        query: userMessage,
      });

      // Add assistant response to chat
      const assistantMessage = {
        type: 'assistant',
        query: userMessage,
        structuredAnswer: response.data.structured_answer || null,
        sources: response.data.sources || [],
        results: response.data.results || [],
        timestamp: new Date().toISOString()
      };
      setRegularChatHistory(prev => [...prev, assistantMessage]);

      // Trigger persona analysis asynchronously
      const allUserQuestions = [...regularChatHistory, newUserMessage]
        .filter(msg => msg.type === 'user')
        .map(msg => msg.content);
      analyzePersona(allUserQuestions);
    } catch (err) {
      const errorMessage = {
        type: 'error',
        content: err.response?.data?.detail || 'Search failed. Please try again.',
        timestamp: new Date().toISOString()
      };
      setRegularChatHistory(prev => [...prev, errorMessage]);
      setError(err.response?.data?.detail || 'Search failed. Please try again.');
    } finally {
      setRegularSearching(false);
    }
  };

  // Analyze user persona based on question history
  const analyzePersona = async (questions) => {
    if (!questions || questions.length === 0) return;

    setAnalyzingPersona(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/analyze-persona/${documentId}`,
        { questions }
      );

      if (response.data.status === 'success' || response.data.status === 'partial_success') {
        setKnowledgeLevel(response.data.knowledge_level);
        setSuggestedQuestions(response.data.suggested_questions || []);
      }
    } catch (err) {
      console.error('Persona analysis failed:', err);
      // Silently fail - don't disrupt user experience
    } finally {
      setAnalyzingPersona(false);
    }
  };

  // Helper to get color for knowledge level
  const getLevelColor = (level) => {
    if (!level) return 'bg-gray-100 text-gray-800';
    const l = level.toLowerCase();
    if (l.includes('beginner')) return 'bg-green-100 text-green-800 border-green-200';
    if (l.includes('intermediate')) return 'bg-blue-100 text-blue-800 border-blue-200';
    if (l.includes('advanced')) return 'bg-purple-100 text-purple-800 border-purple-200';
    if (l.includes('expert')) return 'bg-orange-100 text-orange-800 border-orange-200';
    if (l.includes('master')) return 'bg-red-100 text-red-800 border-red-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Handle suggested question click
  const handleSuggestedQuestionClick = (question) => {
    setRegularQuery(question);
  };

  // Enhanced Search Handler
  const handleEnhancedSearch = async (e) => {
    e.preventDefault();
    if (!enhancedQuery.trim()) return;

    setEnhancedSearching(true);
    setError(null);
    setEnhancedResults(null);

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/enhanced-search/${documentId}`, {
        query: enhancedQuery.trim(),
      });

      setEnhancedResults(response.data);
      setEnhancedResultsList(prev => [{
        query: enhancedQuery.trim(),
        results: response.data,
        timestamp: new Date().toISOString()
      }, ...prev]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Enhanced search failed. Please try again.');
    } finally {
      setEnhancedSearching(false);
    }
  };

  // Summary Handler
  const handleSummarize = async () => {
    setSummarizing(true);
    setError(null);
    setSummary(null);

    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/summary/${documentId}`);
      setSummary(response.data.sections);
    } catch (err) {
      setError(err.response?.data?.detail || 'Summary generation failed. Please try again.');
    } finally {
      setSummarizing(false);
    }
  };

  const formatScore = (score) => {
    return (score * 100).toFixed(1);
  };

  const getScoreColor = (score) => {
    if (score >= 0.8) return 'text-green-600 bg-green-100';
    if (score >= 0.6) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  // Shared markdown components
  const markdownComponents = {
    h1: ({ children }) => <h1 className="text-xl font-bold mb-3 text-gray-900">{children}</h1>,
    h2: ({ children }) => <h2 className="text-lg font-semibold mb-2 text-gray-900">{children}</h2>,
    h3: ({ children }) => <h3 className="text-base font-semibold mb-2 text-gray-900">{children}</h3>,
    p: ({ children }) => <p className="mb-4 last:mb-0 text-gray-700 leading-relaxed">{children}</p>,
    ul: ({ children }) => <ul className="list-disc list-inside mb-4 space-y-1">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal list-inside mb-4 space-y-1">{children}</ol>,
    li: ({ children }) => <li className="text-gray-700">{children}</li>,
    strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
    code: ({ inline, children, className }) => {
      if (inline) {
        return (
          <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono text-gray-800">
            {children}
          </code>
        );
      }
      return (
        <code className={className}>
          {children}
        </code>
      );
    },
    pre: ({ children }) => (
      <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
        {children}
      </pre>
    ),
    img: ({ src, alt, title }) => (
      <div className="my-4 rounded-lg overflow-hidden border border-gray-200 shadow-sm">
        <img
          src={src}
          alt={alt || 'Image'}
          title={title}
          className="w-full h-auto max-w-full object-contain"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        {alt && (
          <div className="bg-gray-50 px-4 py-2 border-t border-gray-200">
            <p className="text-sm text-gray-600 italic">{alt}</p>
          </div>
        )}
      </div>
    ),
    table: ({ children }) => (
      <div className="overflow-x-auto my-4">
        <table className="w-full border-collapse rounded-lg overflow-hidden shadow-sm border border-gray-200">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => <thead className="bg-gray-100">{children}</thead>,
    tbody: ({ children }) => <tbody className="bg-white">{children}</tbody>,
    tr: ({ children }) => <tr className="border-b border-gray-200 hover:bg-gray-50 transition-colors">{children}</tr>,
    th: ({ children }) => (
      <th className="font-semibold text-left px-4 py-3 text-xs uppercase tracking-wider text-gray-900 border border-gray-200">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="px-4 py-3 text-sm text-gray-700 border border-gray-200">
        {children}
      </td>
    ),
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('regular')}
            className={`flex-1 px-6 py-4 text-center font-semibold transition-colors flex items-center justify-center space-x-2 ${activeTab === 'regular'
              ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
          >
            <MessageSquare className="h-5 w-5" />
            <span>Regular Search</span>
          </button>
          <button
            onClick={() => setActiveTab('enhanced')}
            className={`flex-1 px-6 py-4 text-center font-semibold transition-colors flex items-center justify-center space-x-2 ${activeTab === 'enhanced'
              ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
          >
            <Sparkles className="h-5 w-5" />
            <span>Enhanced Search</span>
          </button>
          <button
            onClick={() => setActiveTab('summary')}
            className={`flex-1 px-6 py-4 text-center font-semibold transition-colors flex items-center justify-center space-x-2 ${activeTab === 'summary'
              ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
          >
            <BookOpen className="h-5 w-5" />
            <span>Summary</span>
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Regular Search Tab - Chat Interface */}
          {activeTab === 'regular' && (
            <div className="space-y-4">
              <div className="flex items-center space-x-2 mb-4">
                <MessageSquare className="h-6 w-6 text-primary-600" />
                <h2 className="text-2xl font-bold text-gray-900">Chat with Your Document</h2>
              </div>

              {/* Chat Messages */}
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 h-[500px] overflow-y-auto">
                {regularChatHistory.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <div className="text-center">
                      <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                      <p className="text-lg font-medium">Start a conversation with your document</p>
                      <p className="text-sm mt-2">Ask questions and get instant answers with web-enhanced results</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {regularChatHistory.map((message, index) => (
                      <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] rounded-lg p-4 ${message.type === 'user'
                          ? 'bg-primary-600 text-white'
                          : message.type === 'error'
                            ? 'bg-red-50 text-red-700 border border-red-200'
                            : 'bg-white border border-gray-200 shadow-sm'
                          }`}>
                          {message.type === 'user' && (
                            <div className="font-medium">{message.content}</div>
                          )}
                          {message.type === 'error' && (
                            <div className="flex items-center space-x-2">
                              <AlertCircle className="h-4 w-4" />
                              <span>{message.content}</span>
                            </div>
                          )}
                          {message.type === 'assistant' && (
                            <div className="space-y-4">
                              {message.structuredAnswer && (
                                <div>
                                  <div className="flex items-center space-x-2 mb-2">
                                    <Brain className="h-4 w-4 text-blue-600" />
                                    <span className="text-xs font-semibold text-gray-600 uppercase">Structured Answer</span>
                                  </div>
                                  <div className="prose prose-sm max-w-none">
                                    <ReactMarkdown
                                      remarkPlugins={[remarkGfm]}
                                      rehypePlugins={[rehypeRaw]}
                                      components={markdownComponents}
                                    >
                                      {message.structuredAnswer}
                                    </ReactMarkdown>
                                  </div>
                                </div>
                              )}
                              {message.sources && Array.isArray(message.sources) && message.sources.length > 0 && (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                  <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
                                    <FileText className="h-3 w-3 mr-1" />
                                    Sources ({message.sources.length})
                                  </h4>
                                  <div className="space-y-1">
                                    {message.sources.slice(0, 3).map((source, idx) => (
                                      <a
                                        key={idx}
                                        href={source?.url || '#'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block text-xs text-blue-600 hover:text-blue-800 hover:underline truncate"
                                      >
                                        {idx + 1}. {source?.title || source?.url || 'Source'}
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {message.results && Array.isArray(message.results) && message.results.length > 0 && (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                  <h4 className="text-xs font-semibold text-gray-700 mb-2">Top Chunks ({message.results.length})</h4>
                                  <div className="space-y-2">
                                    {message.results.slice(0, 2).map((result, idx) => (
                                      <div key={idx} className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                                        <span className="font-medium">#{idx + 1}</span> - {result.chunk_text?.substring(0, 150) || result.content?.substring(0, 150) || ''}...
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {regularSearching && (
                      <div className="flex justify-start">
                        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                          <div className="flex items-center space-x-2">
                            <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
                            <span className="text-sm text-gray-600">Searching...</span>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={regularChatEndRef} />
                  </div>
                )}
              </div>

              {/* Chat Input Area */}
              <div className="mt-4">
                {/* Knowledge Level & Suggested Questions (Moved to bottom) */}
                {knowledgeLevel && (
                  <div className="flex items-center space-x-2 mb-3 px-1">
                    <Brain className="h-4 w-4 text-gray-500" />
                    <span className="text-xs text-gray-500 font-medium">Current Level:</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${getLevelColor(knowledgeLevel)}`}>
                      {knowledgeLevel}
                    </span>
                  </div>
                )}

                {suggestedQuestions.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3 px-1">
                    {suggestedQuestions.map((question, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSuggestedQuestionClick(question)}
                        disabled={regularSearching || analyzingPersona}
                        className="px-3 py-1.5 bg-white border border-purple-200 text-sm text-gray-700 rounded-full hover:bg-purple-50 hover:border-purple-300 transition-all shadow-sm flex items-center space-x-1 group disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span>{question}</span>
                        <Send className="h-3 w-3 text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ))}
                    {analyzingPersona && (
                      <div className="flex items-center px-2 py-1.5 text-xs text-gray-500">
                        <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        Generating suggestions...
                      </div>
                    )}
                  </div>
                )}

                <form onSubmit={handleRegularSearch} className="flex items-center space-x-2">
                  <div className="flex-1 relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input
                      type="text"
                      value={regularQuery}
                      onChange={(e) => setRegularQuery(e.target.value)}
                      placeholder={analyzingPersona ? "Analyzing your knowledge level..." : "Ask a question about your document..."}
                      className="w-full pl-12 pr-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors disabled:bg-gray-50 disabled:text-gray-500"
                      disabled={regularSearching || analyzingPersona}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={regularSearching || analyzingPersona || !regularQuery.trim()}
                    className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                  >
                    {regularSearching ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        <Send className="h-5 w-5" />
                        <span>Send</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* Enhanced Search Tab */}
          {activeTab === 'enhanced' && (
            <div className="space-y-4">
              <div className="flex items-center space-x-2 mb-4">
                <Sparkles className="h-6 w-6 text-primary-600" />
                <h2 className="text-2xl font-bold text-gray-900">Enhanced AI Analysis</h2>
              </div>

              <form onSubmit={handleEnhancedSearch} className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    value={enhancedQuery}
                    onChange={(e) => setEnhancedQuery(e.target.value)}
                    placeholder="Ask a question for multi-model AI analysis..."
                    className="w-full pl-12 pr-4 py-4 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
                    disabled={enhancedSearching}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-600">
                    Get comprehensive AI analysis from multiple models (Sonar, Grok 4 Fast, GPT-4o Mini) with Gemini synthesis
                  </p>
                  <button
                    type="submit"
                    disabled={enhancedSearching || !enhancedQuery.trim()}
                    className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                  >
                    {enhancedSearching ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Analyzing...</span>
                      </>
                    ) : (
                      <>
                        <Brain className="h-4 w-4" />
                        <span>Analyze</span>
                      </>
                    )}
                  </button>
                </div>
              </form>

              {/* Enhanced Search Results */}
              {enhancedResults && enhancedResults.small_model_analyses && (
                <div className="space-y-6 mt-6">
                  {/* Final Synthesis */}
                  <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                        <Brain className="h-6 w-6 mr-2 text-blue-600" />
                        AI Analysis Result
                      </h3>
                      <span className="text-sm text-gray-600 bg-blue-50 px-3 py-1 rounded-full">
                        Final Answer
                      </span>
                    </div>
                    <div className="prose prose-sm max-w-none">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]}
                        components={markdownComponents}
                      >
                        {enhancedResults.structured_answer}
                      </ReactMarkdown>
                    </div>
                  </div>

                  {/* Individual Model Analyses */}
                  {enhancedResults.small_model_analyses && Array.isArray(enhancedResults.small_model_analyses) && (
                    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                        <Users className="h-5 w-5 mr-2 text-green-600" />
                        Individual Model Analyses
                      </h3>
                      <div className="grid gap-4 md:grid-cols-1 lg:grid-cols-3">
                        {enhancedResults.small_model_analyses.map((analysis, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-semibold text-gray-900">{analysis.model}</h4>
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                Model {index + 1}
                              </span>
                            </div>
                            <div className="prose prose-sm max-w-none">
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                rehypePlugins={[rehypeRaw]}
                                components={markdownComponents}
                              >
                                {analysis.text}
                              </ReactMarkdown>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Previous Enhanced Searches */}
              {enhancedResultsList.length > 1 && (
                <div className="mt-6 bg-gray-50 rounded-lg border border-gray-200 p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Previous Analyses</h4>
                  <div className="space-y-2">
                    {enhancedResultsList.slice(1).map((item, idx) => (
                      <button
                        key={idx}
                        onClick={() => setEnhancedResults(item.results)}
                        className="w-full text-left px-3 py-2 bg-white hover:bg-gray-100 rounded border border-gray-200 text-sm text-gray-700 transition-colors"
                      >
                        <span className="font-medium">{item.query}</span>
                        <span className="text-xs text-gray-500 ml-2">
                          {new Date(item.timestamp).toLocaleTimeString()}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Summary Tab */}
          {activeTab === 'summary' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <BookOpen className="h-6 w-6 text-primary-600" />
                  <h2 className="text-2xl font-bold text-gray-900">Document Summary</h2>
                </div>
                <button
                  onClick={handleSummarize}
                  disabled={summarizing}
                  className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                >
                  {summarizing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <FileCheck className="h-4 w-4" />
                      <span>Generate Summary</span>
                    </>
                  )}
                </button>
              </div>

              {summary ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 bg-indigo-100 px-3 py-1 rounded-full text-sm">
                      {Object.keys(summary).length} sections
                    </span>
                  </div>

                  <div className="grid gap-4">
                    {Object.entries(summary).map(([heading, data]) => (
                      <div
                        key={heading}
                        className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-shadow"
                      >
                        <div className="mb-4">
                          <h4 className="text-xl font-bold text-gray-900 mb-2">{heading}</h4>
                          {data.description && (
                            <p className="text-sm text-gray-600 italic">{data.description}</p>
                          )}
                        </div>

                        {data.summary && (
                          <div className="prose prose-sm max-w-none">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              rehypePlugins={[rehypeRaw]}
                              components={markdownComponents}
                            >
                              {data.summary}
                            </ReactMarkdown>
                          </div>
                        )}

                        {data.top_5_matches && Array.isArray(data.top_5_matches) && data.top_5_matches.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <h5 className="text-sm font-semibold text-gray-700 mb-2">Top Matches:</h5>
                            <div className="space-y-2">
                              {data.top_5_matches.slice(0, 3).map((match, idx) => (
                                <div key={idx} className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                                  <span className="font-medium">#{match?.rank || idx + 1}</span> - {match?.chunk_preview || ''}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-12 text-center">
                  <BookOpen className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-lg font-medium text-gray-700 mb-2">No summary generated yet</p>
                  <p className="text-sm text-gray-500">Click "Generate Summary" to create a comprehensive summary of your document</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}
    </div>
  );
};

export default SearchInterface;
